// src/components/UpcomingJobsCalendar.js
import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Stack,
  Avatar,
  Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EventIcon from "@mui/icons-material/Event";
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { getStatusColor } from "../utils/statusHelpers";

const isToday = (date) => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

const isTomorrow = (date) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return (
    date.getDate() === tomorrow.getDate() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getFullYear() === tomorrow.getFullYear()
  );
};

function UpcomingJobsCalendar() {
  const theme = useTheme();
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(Date.now());

  // Prevent stale dates if the user leaves the app open overnight
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTrigger(Date.now());
    }, 1000 * 60 * 60); // Refresh every hour
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
    sevenDaysFromNow.setHours(23, 59, 59, 999);

    const upcomingJobsQuery = query(
      collection(db, "jobs"),
      where("scheduledDateTime", ">=", Timestamp.fromDate(today)),
      where("scheduledDateTime", "<=", Timestamp.fromDate(sevenDaysFromNow)),
      orderBy("scheduledDateTime", "asc")
    );

    const unsubscribe = onSnapshot(
      upcomingJobsQuery,
      (snapshot) => {
        const jobsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setJobs(jobsList);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching upcoming jobs:", error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [refreshTrigger]);

  const jobsByDate = useMemo(() => {
    const grouped = {};
    jobs.forEach((job) => {
      const date = job.scheduledDateTime?.toDate();
      if (!date) return;

      const dateKey = date.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short",
      });

      if (!grouped[dateKey]) {
        grouped[dateKey] = { date: date, dateKey: dateKey, jobs: [] };
      }
      grouped[dateKey].jobs.push(job);
    });

    return Object.values(grouped).sort((a, b) => a.date - b.date);
  }, [jobs]);

  const cardBackground = theme.palette.mode === "dark" 
    ? "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(15,23,42,0.95))"
    : "linear-gradient(135deg, rgba(59,130,246,0.05), #ffffff)";

  if (isLoading) {
    return (
      <Card sx={{ background: cardBackground }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box>
              <Skeleton variant="text" width={120} height={32} />
              <Skeleton variant="text" width={80} height={20} />
            </Box>
          </Box>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Skeleton variant="rectangular" height={30} sx={{ borderRadius: 1, mb: 1 }} />
              <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (jobs.length === 0) {
    return (
      <Card 
        elevation={0}
        sx={{ 
          background: "transparent", 
          border: `2px dashed ${theme.palette.divider}`,
          borderRadius: 2 
        }}
      >
        <CardContent>
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Box 
              sx={{ 
                display: 'inline-flex', 
                p: 2, 
                borderRadius: '50%', 
                bgcolor: theme.palette.action.selected,
                mb: 2 
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: 40, color: "text.disabled" }} />
            </Box>
            <Typography variant="h6" color="text.primary" gutterBottom>
              No Upcoming Jobs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Jobs scheduled for the next 7 days will appear here.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ background: cardBackground, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            <CalendarTodayIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Next 7 Days
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {jobs.length} job{jobs.length !== 1 ? "s" : ""} scheduled
            </Typography>
          </Box>
        </Box>

        <List disablePadding>
          {jobsByDate.map((dayGroup, index) => (
            <Box key={dayGroup.dateKey}>
              {/* Date Header */}
              <Box
                sx={{
                  bgcolor: isToday(dayGroup.date) ? "rgba(59,130,246,0.15)" : theme.palette.action.hover,
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <EventIcon fontSize="small" color="primary" />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: isToday(dayGroup.date) ? "primary.main" : "text.primary",
                  }}
                >
                  {dayGroup.dateKey}
                  {isToday(dayGroup.date) && " (Today)"}
                  {isTomorrow(dayGroup.date) && " (Tomorrow)"}
                </Typography>
                <Chip
                  label={`${dayGroup.jobs.length} job${dayGroup.jobs.length !== 1 ? "s" : ""}`}
                  size="small"
                  sx={{ ml: "auto" }}
                />
              </Box>

              {/* Jobs for this date */}
              {dayGroup.jobs.map((job) => (
                <ListItem
                  key={job.id}
                  sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    mb: 1.5,
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'all 0.2s ease-in-out',
                    "&:hover": { 
                      bgcolor: theme.palette.action.hover,
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows[3],
                      cursor: 'pointer'
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                          {job.scheduledDateTime?.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} - {job.jobTemplateName || "Service"}
                        </Typography>
                        <Chip label={job.status || "Pending"} size="small" color={getStatusColor(job.status)} />
                      </Box>
                    }
                    secondary={
                      <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {job.customerName || "N/A"}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DirectionsCarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {job.vehicleReg || job.vehicle || "N/A"}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EngineeringIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {job.engineerName || "Unassigned"}
                          </Typography>
                        </Box>
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
              {index < jobsByDate.length - 1 && <Divider sx={{ my: 2, opacity: 0.5 }} />}
            </Box>
          ))}
        </List>
      </CardContent>
    </Card>
  );
}

export default UpcomingJobsCalendar;